import {
  BLOCKED_PERIOD_OPTIONS,
  CREATABLE_BLOCKED_TYPE_OPTIONS,
  calculateBlockedUntilDate,
  calculateEnforcementEndDate,
  useUploadBlockedRecordFiles,
  calculateBlockedDuration,
} from '@exam-center/blocked-user-management';
import { FormLeavingListener } from '@exam-center/components';
import {
  BlockedRecordBlockedScope,
  BlockedRecordBlockedType,
  BlockedUserIdType,
  SearchBlockedUsersDocument,
  useAddBlockedRecordMutation,
  useGetAllEnabledExamCoursesQuery,
  useGetAllExamCentersQuery,
} from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import { useSnackbar } from '@shared/snackbar';
import { ENGLISH_TITLE_OPTIONS, THAI_TITLE_OPTIONS } from '@shared/utils';
import { useEffect, useMemo, useState } from 'react';

import { BlockedUserCreationForm } from '../../components';
import {
  BLOCKED_USER_CREATION_FIELDS_NAME,
  type BlockedUserCreationFormType,
  useBlockedUserCreationFormContext,
} from '../../providers';

import {
  mapBlockedCheaterUserCreationFormToRequestPayload,
  mapBlockedStaffUserCreationFormToRequestPayload,
} from './mapper';

export default function BlockedUserCreationFormLogic() {
  const navigate = useNavigate();
  const { showPromiseSnackbar } = useSnackbar();
  const [fieldVisibilityStatus, setFieldVisibilityStatus] = useState<{
    enforcementEndDuration: boolean;
    examCourses: boolean;
  }>({
    enforcementEndDuration: false,
    examCourses: false,
  });
  const [fieldDisabledStatus, setFieldDisabledStatus] = useState<{
    enforcementEndDate: boolean;
  }>({
    enforcementEndDate: true,
  });
  const [blockedUntil, setBlockedUntil] = useState<Date | null>(null);

  const formContext = useBlockedUserCreationFormContext();
  const { handleUpload, loading: isUploadingDocuments } = useUploadBlockedRecordFiles();

  const { data: examCoursesData, loading: isLoadingCourses } = useGetAllEnabledExamCoursesQuery();
  const { data: examCentersData, loading: isExamCentersLoading } = useGetAllExamCentersQuery();

  const [addBlockedRecord, { loading: isAdding }] = useAddBlockedRecordMutation({
    onCompleted: (data) => {
      navigate('/admin/blocklist/:blockedUserId', {
        params: { blockedUserId: data.ecAddBlockedRecord.blockedRecord.blockedUserId ?? '' },
      });
    },
    onError: (error) => {
      const graphQLError = (error.graphQLErrors[0].extensions?.details as { code?: string })?.code;
      if (graphQLError === 'INVALID_CITIZEN_CARD_NUMBER') {
        formContext.setError(BLOCKED_USER_CREATION_FIELDS_NAME.personal.idNumber, {
          type: 'manual',
          message: 'เลขบัตรประชาชนไม่ถูกต้อง',
        });
      }
      throw new Error(graphQLError);
    },
    refetchQueries: [SearchBlockedUsersDocument],
  });

  const blockedDurationOptions = [
    ...BLOCKED_PERIOD_OPTIONS,
    {
      value: 'custom',
      label: 'กำหนดเอง',
    },
  ];

  const examCenterOptions = useMemo(() => {
    return [...(examCentersData?.ecGetAllExamCenters.examCenters ?? [])]
      .sort((a, b) => a.examCenterId - b.examCenterId)
      .map((examCenter) => ({
        label: examCenter.code,
        value: examCenter.code,
      }));
  }, [examCentersData]);

  const examCourseOptions = useMemo(() => {
    const examCourses = examCoursesData?.ecGetAllEnabledExamCourses.examCourses ?? [];

    return examCourses.map((course) => ({
      label: `${course.code}: ${course.name}`,
      value: course.code,
    }));
  }, [examCoursesData]);

  const handleAddBlockedRecord = async (data: BlockedUserCreationFormType) => {
    const { fileAttachments } = data.blocklistInfo;

    let payload;

    switch (data.blocklistInfo.blockedType?.value) {
      case BlockedRecordBlockedType.Cheater: {
        const fileAttachmentsUploaded = fileAttachments?.length
          ? await handleUpload(fileAttachments)
          : [];
        const basePayload = mapBlockedCheaterUserCreationFormToRequestPayload(data);
        payload = {
          ...basePayload,
          documentFileNames: fileAttachmentsUploaded,
        };
        break;
      }
      case BlockedRecordBlockedType.Staff:
      default: {
        payload = mapBlockedStaffUserCreationFormToRequestPayload(data);
      }
    }

    return await addBlockedRecord({
      variables: {
        input: payload,
      },
    });
  };

  const handleSubmit = async (data: BlockedUserCreationFormType) => {
    showPromiseSnackbar({
      message: {
        pending: 'กำลังเพิ่มรายชื่อผู้ถูกระงับสิทธิ์สอบ',
        success: 'เพิ่มรายชื่อเรียบร้อยแล้ว',
        error: 'เพิ่มรายชื่อไม่สำเร็จ',
      },
      promise: () => handleAddBlockedRecord(data),
    });
  };

  useEffect(() => {
    // REMARK: clear title when idType changes — a Thai title is invalid for
    // a passport record and vice versa.
    const subscription = formContext.watch((value, { name }) => {
      if (name === BLOCKED_USER_CREATION_FIELDS_NAME.personal.idType && value.personal?.title) {
        formContext.resetField(BLOCKED_USER_CREATION_FIELDS_NAME.personal.title);
      }
    });

    return () => subscription.unsubscribe();
  }, [formContext]);

  useEffect(() => {
    // REMARK: handle visibility
    const subscription = formContext.watch((value, { name }) => {
      if (name === BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.enforcementEndAssign) {
        setFieldVisibilityStatus((prev) => ({
          ...prev,
          enforcementEndDuration:
            (value?.blocklistInfo?.enforcementEnd?.assign as unknown as string) === 'true',
        }));
      }
      if (name === BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.blockedCourseType) {
        setFieldVisibilityStatus((prev) => ({
          ...prev,
          examCourses:
            value.blocklistInfo?.blockedCourses?.type === BlockedRecordBlockedScope.PartialScope,
        }));
      }
    });

    return () => subscription.unsubscribe();
  }, [formContext]);

  useEffect(() => {
    // REMARK: handle disable
    const subscription = formContext.watch((value, { name }) => {
      if (name === BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.enforcementEndDuration) {
        if (value.blocklistInfo?.enforcementEnd?.duration?.value === 'custom') {
          setFieldDisabledStatus((prev) => ({
            ...prev,
            enforcementEndDate: false,
          }));
        } else {
          setFieldDisabledStatus((prev) => ({
            ...prev,
            enforcementEndDate: true,
          }));
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [formContext]);

  useEffect(() => {
    // REMARK: handle clear value
    const subscription = formContext.watch((value, { name }) => {
      // REMARK: handle clear value when enforcementEnd assign changes
      if (name === BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.enforcementEndAssign) {
        if ((value?.blocklistInfo?.enforcementEnd?.assign as unknown as string) === 'false') {
          if (value.blocklistInfo?.enforcementEnd?.duration) {
            formContext.resetField(
              BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.enforcementEndDuration,
            );
          }
          if (value.blocklistInfo?.enforcementEnd?.date) {
            formContext.resetField(
              BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.enforcementEndDate,
            );
          }
        }
      }

      // REMARK: handle clear value when blockedCourse type changes
      if (name === BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.blockedCourseType) {
        if (value.blocklistInfo?.blockedCourses?.type !== BlockedRecordBlockedScope.PartialScope) {
          if (value.blocklistInfo?.blockedCourses?.courses) {
            formContext.resetField(
              BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.blockedCourseCourses,
            );
          }
        }
      }

      // REMARK: handle clear value when enforcementEnd date or blockedAt changes
      if (
        name === BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.enforcementEndDuration ||
        name === BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.blockedAt
      ) {
        const blockedAtCleared = value.blocklistInfo?.blockedAt === undefined;
        const enforcementDateCleared = value.blocklistInfo?.enforcementEnd?.date === undefined;
        const durationCleared = value.blocklistInfo?.enforcementEnd?.duration === undefined;
        const durationInNotCustomAndHasValue =
          value.blocklistInfo?.enforcementEnd?.duration?.value &&
          value.blocklistInfo.enforcementEnd.duration.value !== 'custom';

        if (
          enforcementDateCleared ||
          blockedAtCleared ||
          durationCleared ||
          durationInNotCustomAndHasValue
        ) {
          formContext.resetField(
            BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.enforcementEndDate,
          );
        }
      }

      if (
        name === BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.enforcementEndDate ||
        name === BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.blockedType
      ) {
        if (
          !value.blocklistInfo?.enforcementEnd?.date ||
          !value.blocklistInfo?.blockedType?.value
        ) {
          setBlockedUntil(null);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [formContext]);

  useEffect(() => {
    // REMARK: handle calculation
    const subscription = formContext.watch((value, { name }) => {
      const durationValue = value.blocklistInfo?.enforcementEnd?.duration?.value;
      if (
        durationValue !== 'custom' &&
        (name === BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.blockedAt ||
          name === BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.blockedType ||
          name === BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.enforcementEndDuration)
      ) {
        let enforcementEndDate: Date | null = null;

        if (durationValue && value.blocklistInfo?.blockedAt) {
          enforcementEndDate = calculateEnforcementEndDate({
            blockedAt: value.blocklistInfo.blockedAt ?? null,
            enforcementEndDuration: value.blocklistInfo.enforcementEnd?.duration?.value ?? null,
            enforcementEndDate: value.blocklistInfo.enforcementEnd?.date ?? null,
          });
          formContext.setValue(
            BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.enforcementEndDate,
            enforcementEndDate,
          );
        }

        if (
          durationValue &&
          value.blocklistInfo?.blockedAt &&
          value.blocklistInfo.blockedType?.value
        ) {
          const blockedUntil = calculateBlockedUntilDate({
            blockedType: value.blocklistInfo.blockedType?.value,
            enforcementEndDate,
          });
          setBlockedUntil(blockedUntil);
        }
      }

      if (
        durationValue === 'custom' &&
        (name === BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.blockedType ||
          name === BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.enforcementEndDate)
      ) {
        if (value.blocklistInfo?.enforcementEnd?.date && value.blocklistInfo.blockedType?.value) {
          const blockedUntil = calculateBlockedUntilDate({
            blockedType: value.blocklistInfo.blockedType.value,
            enforcementEndDate: value.blocklistInfo.enforcementEnd.date ?? null,
          });
          setBlockedUntil(blockedUntil);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [formContext]);

  const idTypeValue = formContext.watch(BLOCKED_USER_CREATION_FIELDS_NAME.personal.idType);

  const blockedTypeValue = formContext.watch(
    BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.blockedType,
  )?.value;

  const variant = useMemo(() => {
    switch (blockedTypeValue) {
      case BlockedRecordBlockedType.Cheater:
        return 'cheater';
      case BlockedRecordBlockedType.ExamCenterManager:
        return 'exam-center-manager';
      case BlockedRecordBlockedType.Staff:
      default:
        return 'staff';
    }
  }, [blockedTypeValue]);

  const blockedAt = formContext.watch(BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.blockedAt);

  const blockedEndedAt = formContext.watch(
    BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.enforcementEndDate,
  );

  const blockedDuration = calculateBlockedDuration(
    blockedAt ?? new Date(),
    blockedEndedAt ?? new Date(),
  );

  return (
    <>
      <BlockedUserCreationForm
        onSubmit={formContext.handleSubmit(handleSubmit)}
        submitting={formContext.formState.isSubmitting || isAdding || isUploadingDocuments}
        variant={variant}
        fields={{
          idType: {
            name: BLOCKED_USER_CREATION_FIELDS_NAME.personal.idType,
            required: true,
          },
          idNumber: {
            name: BLOCKED_USER_CREATION_FIELDS_NAME.personal.idNumber,
            required: true,
          },
          title: {
            name: BLOCKED_USER_CREATION_FIELDS_NAME.personal.title,
            options:
              idTypeValue === BlockedUserIdType.Passport
                ? ENGLISH_TITLE_OPTIONS
                : THAI_TITLE_OPTIONS,
            required: true,
          },
          firstName: { name: BLOCKED_USER_CREATION_FIELDS_NAME.personal.firstName, required: true },
          middleName: { name: BLOCKED_USER_CREATION_FIELDS_NAME.personal.middleName },
          lastName: { name: BLOCKED_USER_CREATION_FIELDS_NAME.personal.lastName, required: true },
          email: { name: BLOCKED_USER_CREATION_FIELDS_NAME.personal.email, required: true },
          phoneNumber: { name: BLOCKED_USER_CREATION_FIELDS_NAME.personal.phoneNumber },
          blockedType: {
            name: BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.blockedType,
            options: CREATABLE_BLOCKED_TYPE_OPTIONS,
            required: true,
          },
          position: { name: BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.position },
          department: { name: BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.department },
          blockedAt: {
            name: BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.blockedAt,
            required: true,
            blockedDuration,
          },
          enforcementEnd: {
            assign: {
              name: BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.enforcementEndAssign,
              error: formContext.formState.errors.blocklistInfo?.enforcementEnd?.assign?.message,
            },
            duration: {
              name: BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.enforcementEndDuration,
              options: blockedDurationOptions,
              hidden: !fieldVisibilityStatus.enforcementEndDuration,
              required: true,
            },
            date: {
              name: BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.enforcementEndDate,
              required: true,
              disabled: fieldDisabledStatus.enforcementEndDate,
            },
            blockedUntil: blockedUntil ?? undefined,
          },
          blockedCourse: {
            type: { name: BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.blockedCourseType },
            courses: {
              name: BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.blockedCourseCourses,
              options: examCourseOptions,
              required: true,
              hidden: !fieldVisibilityStatus.examCourses,
              loading: isLoadingCourses,
            },
          },
          examCenter: {
            name: BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.examCenter,
            options: examCenterOptions,
            loading: isExamCentersLoading,
            required: true,
          },
          examDate: { name: BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.examDate },
          examRound: { name: BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.examRound },
          examStartTime: { name: BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.examStartTime },
          examEndTime: { name: BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.examEndTime },
          fileDocuments: { name: BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.fileDocuments },
          reason: { name: BLOCKED_USER_CREATION_FIELDS_NAME.blocklistInfo.reason },
        }}
      />
      <FormLeavingListener onSubmit={handleSubmit} />
    </>
  );
}
